import React, { useState } from 'react';
import { X, MessageCircle, Copy, Check, Share2, Calendar, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { AvatarGroup } from '../ui/Avatar';
import { fullAddress } from '../../lib/utils';

export default function ShareSheet({ cell, network, onClose }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = `${window.location.origin}/convite/${cell.id}`;
  const leaderAvatars = cell.leader.avatar2 ? [cell.leader.avatar, cell.leader.avatar2] : [cell.leader.avatar];
  const message =
    `Você está convidado(a) para a *${cell.name}* (${network?.name || ''}) da Comunidade Águas Vivas Caratinga!\n\n` +
    `Quando: ${cell.day} às ${cell.time}\n` +
    `Onde: ${fullAddress(cell.address)}\n\n` +
    `Veja o convite: ${shareUrl}`;


  const whatsappShare = `https://wa.me/?text=${encodeURIComponent(message)}`;

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copiado!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Não foi possível copiar o link.');
    }
  }

  async function nativeShare() {
    if (navigator.share) {
      try {
        await navigator.share({ title: cell.name, text: message, url: shareUrl });
      } catch {
        /* user cancelled */
      }
    } else {
      copyLink();
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center md:items-center md:p-6" data-testid="share-sheet">
      <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-[480px] animate-sheet-up rounded-t-2xl bg-white p-5 pb-8 shadow-lift md:max-w-lg md:rounded-2xl md:animate-fade-up">
        <div className="mx-auto mb-4 h-1.5 w-10 rounded-full bg-slate-200 md:hidden" />
        <button onClick={onClose} className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-ink-soft hover:bg-slate-200" data-testid="share-close" aria-label="Fechar">
          <X size={18} />
        </button>

        <h3 className="pr-10 font-heading text-xl font-extrabold text-ink">Convidar para a célula</h3>
        <p className="mt-0.5 text-sm text-ink-soft">Compartilhe o convite e traga alguém especial.</p>

        {/* preview card */}
        <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200">
          <div className="bg-gradient-to-br from-primary to-primary-dark p-4 text-white">
            <span className="text-[11px] font-bold uppercase tracking-wider text-white/80">{network?.name}</span>
            <p className="font-heading text-lg font-extrabold leading-tight">{cell.name}</p>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-white/90">
              <span className="inline-flex items-center gap-1.5"><Calendar size={14} /> {cell.day}</span>
              <span className="inline-flex items-center gap-1.5"><Clock size={14} /> {cell.time}</span>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-white p-3">
            <AvatarGroup people={[...leaderAvatars, cell.host.avatar].filter(Boolean)} size={34} />
            <p className="text-xs text-ink-soft">{fullAddress(cell.address)}</p>
          </div>
        </div>

        <div className="mt-4 space-y-2.5">
          <a
            href={whatsappShare}
            target="_blank"
            rel="noreferrer"
            className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#25D366] font-heading font-semibold text-white shadow-soft transition-transform duration-200 hover:-translate-y-0.5"
            data-testid="share-whatsapp"
          >
            <MessageCircle size={18} /> Compartilhar no WhatsApp
          </a>
          <button
            onClick={copyLink}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-full border border-slate-200 bg-white font-heading font-semibold text-ink transition-transform duration-200 hover:-translate-y-0.5"
            data-testid="share-copy"
          >
            {copied ? <Check size={18} className="text-emerald-500" /> : <Copy size={18} />}
            {copied ? 'Link copiado' : 'Copiar link'}
          </button>
          <button
            onClick={nativeShare}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-primary-light font-heading font-semibold text-primary-dark transition-transform duration-200 hover:-translate-y-0.5"
            data-testid="share-native"
          >
            <Share2 size={18} /> Outras opções
          </button>
        </div>
      </div>
    </div>
  );
}
